import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, ImagePlus, Loader2, ExternalLink, Star, MessageSquareQuote, FolderOpen, Tags } from "lucide-react";
import {
  fetchMe, logout, listProjects, uploadProject, deleteProject,
  listReviews, deleteReview, updateProjectCategory,
} from "@/lib/api";
import { PROJECT_CATEGORIES, DEFAULT_CATEGORY } from "@/lib/projectCategories";
import { AdminImageCard } from "@/components/admin/AdminImageCard";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [images, setImages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(DEFAULT_CATEGORY);
  const [activeTab, setActiveTab] = useState(DEFAULT_CATEGORY);
  const [onlyUntagged, setOnlyUntagged] = useState(false);
  const inputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const [imgs, revs] = await Promise.all([listProjects(), listReviews()]);
      setImages(imgs);
      setReviews(revs);
    } catch (err) {
      console.error("Failed to load admin data:", err);
      toast.error("Kunde inte ladda data");
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await fetchMe();
        if (!alive) return;
        setUser(me);
        await reload();
      } catch {
        if (alive) nav("/admin/login");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [nav, reload]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) {
      toast.error("Välj bildfiler (JPG, PNG, WEBP)");
      return;
    }
    setUploading(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      setProgress(0);
      try {
        await uploadProject(f, uploadCategory, setProgress);
        ok += 1;
      } catch (err) {
        fail += 1;
        const detail = err?.response?.data?.detail;
        toast.error(`${f.name}: ${typeof detail === "string" ? detail : "uppladdning misslyckades"}`);
      }
    }
    setUploading(false);
    setProgress(0);
    if (ok) {
      toast.success(`${ok} ${ok === 1 ? "bild uppladdad" : "bilder uppladdade"} till ${PROJECT_CATEGORIES.find((c) => c.slug === uploadCategory)?.title}`);
      setActiveTab(uploadCategory);
    }
    if (fail === 0 && ok === 0) toast.info("Inga bilder uppladdade");
    await reload();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = async (id) => {
    if (!window.confirm("Radera bilden permanent?")) return;
    setDeletingId(id);
    try {
      await deleteProject(id);
      setImages((prev) => prev.filter((x) => x.id !== id));
      toast.success("Bild raderad");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Kunde inte radera");
    } finally {
      setDeletingId(null);
    }
  };

  const moveImage = async (id, newCategory) => {
    setMovingId(id);
    try {
      await updateProjectCategory(id, newCategory);
      setImages((prev) => prev.map((x) => (x.id === id ? { ...x, category: newCategory } : x)));
      const title = PROJECT_CATEGORIES.find((c) => c.slug === newCategory)?.title;
      toast.success(`Flyttad till ${title}`);
    } catch (err) {
      console.error("Move failed:", err);
      toast.error("Kunde inte flytta bilden");
    } finally {
      setMovingId(null);
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm("Radera recensionen permanent?")) return;
    setDeletingReviewId(id);
    try {
      await deleteReview(id);
      setReviews((prev) => prev.filter((x) => x.id !== id));
      toast.success("Recension raderad");
    } catch (err) {
      console.error("Delete review failed:", err);
      toast.error("Kunde inte radera recension");
    } finally {
      setDeletingReviewId(null);
    }
  };

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  // Group images by category
  const grouped = useMemo(() => {
    const map = {};
    for (const cat of PROJECT_CATEGORIES) map[cat.slug] = [];
    for (const img of images) {
      const slug = img.category && map[img.category] ? img.category : PROJECT_CATEGORIES[0].slug;
      map[slug].push(img);
    }
    return map;
  }, [images]);

  const folderImages = grouped[activeTab] || [];
  const untaggedCount = folderImages.filter((x) => !x.service_tags?.length).length;
  const activeImages = onlyUntagged ? folderImages.filter((x) => !x.service_tags?.length) : folderImages;
  const activeCategory = PROJECT_CATEGORIES.find((c) => c.slug === activeTab);

  const onSlideshowSaved = (updated) => {
    setImages((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
  };

  return (
    <div
      data-testid="admin-dashboard-page"
      className="min-h-screen"
      style={{ background: "linear-gradient(180deg, #0B132B 0%, #1a1035 45%, #6b0e18 100%)" }}
    >
      {/* Header */}
      <header className="border-b-2 border-white/15 bg-[#0B132B]/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-display font-black text-xl md:text-2xl tracking-tight text-white">
              SWEDCRO <span className="text-[#FFB3B3]">/ admin</span>
            </div>
            {user && (
              <div className="font-body text-xs text-white/60 mt-0.5" data-testid="admin-user-email">
                {user.email}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site"
              className="hidden sm:inline-flex items-center gap-2 border-2 border-white/40 text-white font-display font-bold text-xs tracking-[0.15em] uppercase px-4 py-2.5 hover:border-white hover:bg-white/10 transition-colors"
            >
              <ExternalLink size={14} /> Se sajten
            </a>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border-2 border-[#D92525] bg-[#D92525] text-white font-display font-bold text-xs tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-transparent hover:text-white transition-colors"
            >
              <LogOut size={14} /> Logga ut
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#FFB3B3]">
            <span className="w-8 h-[2px] bg-[#FFB3B3]" /> Projektbilder
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white mt-3">
            Hantera <span className="text-[#FFB3B3]">bilder.</span>
          </h1>
          <p className="font-body text-white/70 mt-3 max-w-2xl">
            Välj en mapp, ladda upp bilder och flytta dem mellan mapparna. Sajten uppdateras automatiskt.
          </p>
        </div>

        {/* Category selector for upload */}
        <div className="mb-4">
          <div className="font-display font-bold text-xs uppercase tracking-[0.15em] text-white/70 mb-2">
            1. Välj mapp för uppladdning
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {PROJECT_CATEGORIES.map((cat) => {
              const count = grouped[cat.slug]?.length || 0;
              const isActive = uploadCategory === cat.slug;
              return (
                <button
                  key={cat.slug}
                  type="button"
                  data-testid={`admin-upload-cat-${cat.slug}`}
                  onClick={() => setUploadCategory(cat.slug)}
                  className={`text-left border-2 px-4 py-3 transition-all ${
                    isActive
                      ? "border-[#D92525] bg-[#D92525]/15 shadow-[4px_4px_0_#D92525]"
                      : "border-white/20 bg-white/[0.03] hover:border-white/60"
                  }`}
                >
                  <div className="font-display font-black text-white text-sm md:text-base tracking-tight truncate">
                    {cat.title}
                  </div>
                  <div className={`font-body text-xs mt-0.5 ${isActive ? "text-[#FFB3B3]" : "text-white/50"}`}>
                    {count} {count === 1 ? "bild" : "bilder"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Dropzone */}
        <div className="mt-4">
          <div className="font-display font-bold text-xs uppercase tracking-[0.15em] text-white/70 mb-2">
            2. Ladda upp bilder till <span className="text-[#FFB3B3]">{PROJECT_CATEGORIES.find((c) => c.slug === uploadCategory)?.title}</span>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="admin-upload-dropzone"
            className={`border-2 border-dashed p-10 md:p-14 text-center cursor-pointer transition-colors ${
              dragOver ? "border-[#D92525] bg-[#D92525]/10" : "border-white/40 bg-white/[0.03] hover:border-white hover:bg-white/[0.06]"
            } ${uploading ? "pointer-events-none opacity-70" : ""}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              data-testid="admin-upload-input"
              onChange={(e) => handleFiles(e.target.files)}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={40} className="text-white animate-spin" />
                <div className="font-display font-bold text-white text-lg">Laddar upp... {progress}%</div>
                <div className="w-full max-w-md h-2 bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#D92525] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 border-2 border-white/50 flex items-center justify-center">
                  <ImagePlus size={28} className="text-white" strokeWidth={1.8} />
                </div>
                <div className="font-display font-black text-white text-xl md:text-2xl tracking-tight">
                  Släpp bilder här
                </div>
                <div className="font-body text-white/70 text-sm">
                  eller klicka för att välja från datorn / telefonen
                </div>
                <div className="font-body text-white/40 text-xs mt-2">
                  JPG · PNG · WEBP · max 15MB per bild
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Gallery tabs */}
        <div className="mt-12 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display font-black text-white text-2xl tracking-tight">
            Uppladdade <span className="text-[#FFB3B3]">bilder</span>
            {" "}
            <span className="text-white/50 font-body font-normal text-base" data-testid="admin-images-count">
              ({images.length} totalt)
            </span>
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 cursor-pointer font-body text-sm text-white">
              <input
                type="checkbox"
                checked={onlyUntagged}
                onChange={(e) => setOnlyUntagged(e.target.checked)}
                data-testid="admin-filter-untagged"
                className="w-4 h-4 accent-[#D92525]"
              />
              Visa bara otaggade
              <span className="text-white/50 text-xs" data-testid="admin-untagged-count">({untaggedCount})</span>
            </label>
            <button
              onClick={reload}
              data-testid="admin-refresh"
              className="font-display font-bold text-xs tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors"
            >
              Uppdatera
            </button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="mt-4 border-b-2 border-white/10 flex flex-wrap gap-1">
          {PROJECT_CATEGORIES.map((cat) => {
            const count = grouped[cat.slug]?.length || 0;
            const isActive = activeTab === cat.slug;
            return (
              <button
                key={cat.slug}
                type="button"
                data-testid={`admin-tab-${cat.slug}`}
                onClick={() => setActiveTab(cat.slug)}
                className={`px-4 py-3 font-display font-bold text-xs md:text-sm tracking-[0.12em] uppercase transition-all border-b-2 -mb-[2px] ${
                  isActive
                    ? "border-[#D92525] text-white"
                    : "border-transparent text-white/50 hover:text-white/80"
                }`}
              >
                {cat.title} <span className={isActive ? "text-[#FFB3B3]" : "text-white/40"}>({count})</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="mt-8 text-white/70 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Laddar...
          </div>
        ) : activeImages.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="mt-8 border-2 border-white/15 p-10 text-center bg-white/[0.02]"
          >
            <FolderOpen size={40} className="text-white/40 mx-auto" strokeWidth={1.6} />
            <div className="font-display font-bold text-white text-lg mt-3">
              {onlyUntagged && folderImages.length ? "Alla bilder är taggade" : `Inga bilder i ${activeCategory?.title}`}
            </div>
            <div className="font-body text-white/60 text-sm mt-1">
              {onlyUntagged && folderImages.length
                ? "Avmarkera filtret för att se alla bilder i mappen."
                : "Välj denna mapp ovan och ladda upp din första bild."}
            </div>
          </div>
        ) : (
          <>
          <p className="mt-6 font-body text-white/60 text-sm flex items-center gap-2">
            <Tags size={14} className="text-[#FFB3B3]" />
            Välj under varje bild vilka tjänster den visas i på startsidan. Ändringar sparas automatiskt.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeImages.map((img) => (
              <AdminImageCard
                key={img.id}
                img={img}
                onUpdated={onSlideshowSaved}
                onMove={moveImage}
                onDelete={removeImage}
                moving={movingId === img.id}
                deleting={deletingId === img.id}
              />
            ))}
          </div>
          </>
        )}

        {/* Reviews management */}
        <div className="mt-16 pt-12 border-t-2 border-white/10">
          <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#FFB3B3]">
            <span className="w-8 h-[2px] bg-[#FFB3B3]" /> Recensioner
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-white mt-3">
            Hantera <span className="text-[#FFB3B3]">recensioner.</span>
            {" "}
            <span className="text-white/50 font-body font-normal text-base" data-testid="admin-reviews-count">
              ({reviews.length})
            </span>
          </h2>
          <p className="font-body text-white/70 mt-3 max-w-xl">
            Radera oönskade eller test-recensioner. Riktiga kundrecensioner visas automatiskt på sajten.
          </p>

          {reviews.length === 0 ? (
            <div
              data-testid="admin-reviews-empty"
              className="mt-6 border-2 border-white/15 p-10 text-center bg-white/[0.02]"
            >
              <MessageSquareQuote size={32} className="text-white/50 mx-auto" strokeWidth={1.8} />
              <div className="font-display font-bold text-white text-lg mt-3">Inga recensioner ännu</div>
              <div className="font-body text-white/60 text-sm mt-1">Recensioner som skickas in från sajten dyker upp här.</div>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {reviews.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-review-${r.id}`}
                  className="bg-white/[0.04] border-2 border-white/15 p-5 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-white text-lg tracking-tight">{r.name}</div>
                      {r.place && (
                        <div className="font-body text-xs text-white/50 mt-0.5">{r.place}</div>
                      )}
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <Star
                          key={k}
                          size={14}
                          fill={k < (r.rating || 5) ? "#D92525" : "transparent"}
                          stroke="#D92525"
                        />
                      ))}
                    </div>
                  </div>
                  <p className="font-body text-white/80 text-sm mt-3 leading-relaxed flex-1">
                    &ldquo;{r.text}&rdquo;
                  </p>
                  <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between gap-3">
                    <div className="font-body text-xs text-white/40">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("sv-SE") : ""}
                    </div>
                    <button
                      onClick={() => removeReview(r.id)}
                      disabled={deletingReviewId === r.id}
                      data-testid={`admin-review-delete-${r.id}`}
                      className="inline-flex items-center gap-2 border-2 border-[#D92525] text-[#FFB3B3] hover:bg-[#D92525] hover:text-white font-display font-bold text-xs tracking-[0.15em] uppercase px-3 py-2 transition-colors disabled:opacity-60"
                    >
                      {deletingReviewId === r.id ? (
                        <><Loader2 size={12} className="animate-spin" /> Raderar</>
                      ) : (
                        <><Trash2 size={12} /> Radera</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
